/**
 * KidsJoy storefront extras.
 * - <kj-reveal-on-scroll target="selector">: toggles [data-visible] on itself when the target scrolls out of view
 *   (used by the back-to-top button and the sticky add-to-cart bar).
 * - <kj-countdown>: counts down to the end of the shopper's day and writes hh/mm/ss into [data-h], [data-m], [data-s].
 */
class KjRevealOnScroll extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const selector = this.getAttribute('target');
    const target = selector ? document.querySelector(selector) : null;
    if (!target) {
      // No target: show once the page has scrolled one screen.
      const onScroll = () => this.toggleAttribute('data-visible', window.scrollY > window.innerHeight * 0.8);
      this._onScroll = onScroll;
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }
    this.#observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', passed);
    });
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
  }
}

class KjCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const now = new Date();
    const end = new Date(now);
    end.setHours(24, 0, 0, 0);
    let left = Math.max(0, Math.floor((end - now) / 1000));
    const h = Math.floor(left / 3600);
    left -= h * 3600;
    const m = Math.floor(left / 60);
    const s = left - m * 60;
    const pad = (n) => String(n).padStart(2, '0');
    const set = (sel, v) => { const el = this.querySelector(sel); if (el) el.textContent = pad(v); };
    set('[data-h]', h);
    set('[data-m]', m);
    set('[data-s]', s);
  }
}

if (!customElements.get('kj-reveal-on-scroll')) customElements.define('kj-reveal-on-scroll', KjRevealOnScroll);
if (!customElements.get('kj-countdown')) customElements.define('kj-countdown', KjCountdown);

/* Copy a discount code to the clipboard. */
if (!customElements.get('kj-copy-code')) {
  customElements.define('kj-copy-code', class extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const code = this.querySelector('[data-code]');
      const status = this.querySelector('[role="status"]');
      if (!button || !code) return;
      const label = button.textContent;
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          button.textContent = button.dataset.copied || label;
          if (status) status.textContent = button.dataset.copied || '';
          clearTimeout(this._t);
          this._t = setTimeout(() => { button.textContent = label; if (status) status.textContent = ''; }, 2000);
        } catch (e) {
          const range = document.createRange();
          range.selectNodeContents(code);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      });
    }
  });
}
